<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div class="feature-flags">
    Feature flags
    <ul>
      <li
        v-for="[key, value] in entries"
        :key="key"
      >
        <label :for="key">
          <input
            v-if="typeof value === 'boolean'"
            :id="key"
            :checked="value"
            class="checkbox"
            :name="key"
            type="checkbox"
            @change="setValue(key, !value)"
          >
          <input
            v-else
            :id="key"
            :checked="true"
            class="checkbox"
            :disabled="true"
            :name="key"
            type="checkbox"
          >
          {{ key }}
          <input
            v-if="typeof value === 'number'"
            :id="`${key}-value`"
            class="value"
            :name="key"
            type="number"
            :value="value"
            @change="(event) => setValue(key, Number((event.target as HTMLInputElement).value))"
          >
        </label>
      </li>
      <li v-if="projectId && isDummyAlgorithm">
        <a
          class="dummy-link"
          :href="`/hub/api/v1/alpha/projects/${projectId}/dummy`"
          @click.prevent="callDummyEndpoint"
        >
          Fill project with dummy data
        </a>
      </li>
      <li v-if="isAgentOverview">
        <a
          class="dummy-link"
          :href="`/hub/api/v1/alpha/platform-connections/dummy`"
          @click.prevent="callPlatformConnectionsDummyEndpoint"
        >
          Fill platform connections with dummy data
        </a>
        <br>
        <label>
          Total layers:
          <input
            v-model="generatePlatformConnections.totalLayers"
            class="value"
            max="10"
            min="1"
            type="number"
          >
        </label>
        <br>
        <label>
          Connections per layer:
          <input
            v-model="generatePlatformConnections.connectionsPerLayer"
            class="value"
            max="10"
            min="1"
            type="number"
          >
        </label>
        <br>
        <label>
          Include connected:
          <input
            v-model="generatePlatformConnections.includeConnected"
            class="checkbox"
            type="checkbox"
          >
        </label>
        <br>
        <label>
          Include disconnected:
          <input
            v-model="generatePlatformConnections.includeDisconnected"
            class="checkbox"
            type="checkbox"
          >
        </label>
        <br>
        <label>
          Include unknown:
          <input
            v-model="generatePlatformConnections.includeUnknown"
            class="checkbox"
            type="checkbox"
          >
        </label>
        <br>
        <label>
          Include unhealthy:
          <input
            v-model="generatePlatformConnections.includeUnhealthy"
            class="checkbox"
            type="checkbox"
          >
        </label>
      </li>
    </ul>
    Changes made here will be persisted in session storage and will be available until the browser is closed.
    <br>
    <br>
    <q-btn
      class="q-px-md q-py-none no-hover reset"
      dense
      no-caps
      outline
      @click="reset"
    >
      Reset
    </q-btn>
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted,
  ref,
} from 'vue'
import { useRoute } from 'vue-router'
import { features } from '~services/features'
import { useProjectsStore } from '~stores/projectsStore/useProjectsStore'


const route = useRoute()
const { loadProjectByName } = useProjectsStore()
const projectId = ref<string | undefined>()
const isDummyAlgorithm = ref(false)
const isAgentOverview = ref(false)
const generatePlatformConnections = ref({
  connectionsPerLayer: 3,
  includeConnected: false,
  includeDisconnected: false,
  includeUnhealthy: false,
  includeUnknown: false,
  totalLayers: 7,
})

onMounted(async () => {
  const projectName = route.params.projectName as string
  if (projectName) {
    const project = await loadProjectByName(projectName)
    if (project) {
      projectId.value = project.id
      isDummyAlgorithm.value = project.defaultAlgorithmId === 'ai.robovision.algorithms.dummy'
    }
  }
  // Dummy agent overview data
  isAgentOverview.value = route.path.includes('platform-connections') || route.path.includes('inference-monitoring')
})

const {
  entries,
  reset,
  setValue,
} = features

async function callDummyEndpoint () {
  if (!projectId.value) return
  try {
    const response = await fetch(`/hub/api/v1/alpha/projects/${projectId.value}/dummy`, {
      method: 'POST',
    })
    if (!response.ok) {
      throw new Error('Failed to call dummy endpoint')
    }
  } catch (error) {
    console.error('Error calling dummy endpoint:', error)
  }
}

async function callPlatformConnectionsDummyEndpoint () {
  try {
    const response = await fetch('/hub/api/v1/alpha/platform-connections/dummy', {
      body: JSON.stringify(generatePlatformConnections.value),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    if (!response.ok) {
      throw new Error('Failed to call platform connections dummy endpoint')
    } else {
      // Refresh the page
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  } catch (error) {
    console.error('Error calling platform connections dummy endpoint:', error)
  }
}
</script>

<style lang="sass" scoped>
@use 'sass:color'

.feature-flags
  .checkbox
    vertical-align: middle

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button
      -webkit-appearance: none
      margin: 0

  input[type="number"]
      -moz-appearance: textfield

  .value
    width: 6ch
    background: color.scale(#FFC, $lightness: 5%, $space: hsl)
    border-radius: 5px
    border: 1px solid color.scale(#FFC, $lightness: -45%, $space: hsl)
    margin-left: 5px

  .reset
    font-family: sans-serif
    font-size: 10pt
    font-weight: normal

    ::v-deep(.q-btn__content)
      color: black

  .dummy-link
    color: #1976D2
    text-decoration: none
    &:hover
      text-decoration: underline
</style>
